<template>
  <v-main>

    <v-container
        id="users-container"
        fluid
        tag="section"
    >

      <base-material-card
          icon="mdi-account-multiple-outline"
          class="px-5 py-5"
      >

        <shared-page-messages :error-messages="errorMessages"
                              :warning-messages="warningMessages"
                              :success-messages="successMessages">
        </shared-page-messages>


        <v-card-text>
          <v-card-title class="pr-0">
            <v-row>

              <v-col
                  cols="12"
                  sm="12"
                  class="float-right p-0 mb-5"
              >

                <v-dialog
                    v-model="filtersDialog"
                    max-width="400"
                >

                  <template v-slot:activator="{ on, attrs }">

                    <v-btn
                        @click="exportCsv"
                        color="primary"
                        small
                        class="float-right mr-0"
                    >
                      CSV Export
                    </v-btn>

                    <v-btn
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        class="float-right mr-0"
                    >
                      <v-icon size="small">mdi-filter</v-icon> Filters
                    </v-btn>
                  </template>


                  <form @submit.prevent="applyFilters">

                    <v-card>
                      <v-card-title class="text-h5">
                        Member Filters
                      </v-card-title>

                      <v-card-text>
                        <v-row dense>
                          <v-col
                              cols="12"
                              sm="12"
                          >
                            <v-select
                                v-model="filter.userStatuses"
                                :items="computedUserStatusItems"
                                label="Member Status"
                                dense
                                multiple
                            ></v-select>
                          </v-col>
                        </v-row>
                        <v-row dense>
                          <v-col
                              cols="12"
                              sm="12"
                          >
                            <v-text-field
                                v-model="filter.firstName"
                                label="First Name"
                                dense
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row dense>
                          <v-col
                              cols="12"
                              sm="12"
                          >
                            <v-text-field
                                v-model="filter.lastName"
                                label="Last Name"
                                dense
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row dense>
                          <v-col
                              cols="4"
                              sm="4"
                          >
                            <v-select
                                v-model="filter.itemsPerPage"
                                :items="[25, 50, 100, 200, 500]"
                                label="Items Per Page"
                                dense
                            >

                            </v-select>
                          </v-col>
                        </v-row>

                      </v-card-text>

                      <v-card-actions class="pt-0">
                        <v-spacer></v-spacer>
                          <v-btn
                              small
                              color="primary"
                              @click="filtersDialog = false"
                              class="float-right mx-0"
                          >
                            Close
                          </v-btn>
                          <v-btn
                              small
                              color="primary"
                              type="submit"
                              class="float-right mx-2"
                          >
                            Apply
                          </v-btn>
                          <v-btn
                              small
                              color="primary"
                              class="float-right mx-0"
                              @click="resetFilters"
                          >
                            Reset
                          </v-btn>
                      </v-card-actions>

                    </v-card>

                  </form>

                </v-dialog>
              </v-col>
            </v-row>
          </v-card-title>

          <v-simple-table
              :items="users"
              :items-per-page="filter.itemsPerPage"
              class="elevation-1"
              dense
              loading-text="Loading... Please wait"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-subtitle-2 member-name">Name</th>
                  <th class="text-subtitle-2 member-location">Location</th>
                  <th class="text-subtitle-2 member-phone-number">Phone #</th>
                  <th class="text-subtitle-2 member-email">Email Address</th>
                  <th class="text-subtitle-2 member-status">Status</th>
                  <th class="text-subtitle-2 member-updated">Updated</th>
                  <th class="text-subtitle-2 member-review" v-if="isReviewAction">Action</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="user in users"
                    :key="user.id">

                  <td>
                    <router-link :to="{ name: 'view_member', params: {memberId: user.id} }">
                      {{ user.names }}
                    </router-link>
                  </td>
                  <td> {{ user.city }}, {{ user.state }} </td>
                  <td> {{ user.phoneNumber }} </td>
                  <td> {{ user.email }} </td>
                  <td> {{ userStatusMap[user.status] }} </td>
                  <td>
                    <span v-if="user.updated">{{ user.updated | formatDate }}</span>
                    <span v-else>{{ user.created | formatDate }}</span>
                  </td>
                  <td class="member-action" v-if="isReviewAction">
                    <router-link :to="{ name: 'edit_member', params: {memberId: user.id} }">
                      Review
                    </router-link>
                  </td>

                </tr>

              </tbody>
            </template>
          </v-simple-table>

          <div class="text-center mt-3">
            <v-row>
              <v-col class="page-counts text-body-2" cols="12">
                {{ computedPageStart }} - {{ computedPageEnd }} of {{ searchResponse.page.totalElements }}
              </v-col>
            </v-row>

            <v-pagination
                v-model="filter.page"
                :length="searchResponse.page.totalPages || 1"
                :total-visible="8"
                @input="fetchMembers"
            ></v-pagination>
          </div>

        </v-card-text>


      </base-material-card>


    </v-container>

  </v-main>
</template>

<script>
import api from '@/services/api'
import constants from '@/utils/constants'
import exportUtils from '@/utils/export'

import SharedPageMessages from '../shared/PageMessages.vue'
import moment from 'moment-timezone'

export default {
  name: 'CoreMembersView',

  components: {
    SharedPageMessages
  },

  created() {
    this.fetchMembers()
  },

  props: [
      'userStatuses',
      'isReviewAction'
  ],

  computed: {
    users() {
      return this.searchResponse.content
    },
    computedUserStatusItems: {
      get: function () {
        return Object.keys(this.userStatusMap)
            .map((key) => ({value: key, text: this.userStatusMap[key]}))
      }
    },
    computedPageStart: function () {
      const page = this.searchResponse.page
      if (page.totalElements === 0) {
        return 0
      }

      return (page.number * page.size) + 1
    },

    computedPageEnd: function () {
      const page = this.searchResponse.page
      if (page.totalPages === 0) {
        return 0
      }
      if (page.totalPages === 1) {
        return page.totalElements
      }

      return (page.number * page.size) + page.size
    }
  },

  watch: {
    searchResponse(val) {
      if (val.empty) {
        this.warningMessages.push("Sorry, no members matching your filter criteria could be found. Please adjust your filters.")
      }
    },
    errorMessages: function (val) {
      if (val.length > 0) {
        scroll(0, 0)
      }
    },
    successMessages: function (val) {
      if (val.length > 0) {
        scroll(0, 0)
      }
    },
    warningMessages: function (val) {
      if (val.length > 0) {
        scroll(0, 0)
      }
    }
  },

  methods: {
    clearMessages() {
      this.successMessages = []
      this.errorMessages = []
      this.warningMessages = []
    },
    applyFilters() {
      this.filtersDialog = false
      this.clearMessages()
      this.fetchMembers()
    },
    resetFilters() {
      this.filtersDialog = false
      this.clearMessages()
      this.filter = {
        userStatuses: this.userStatuses,
        firstName: '',
        lastName: '',
        itemsPerPage: 25,
        page: 1,
      }
      this.fetchMembers()
    },

    async fetchMembers(page = 1) {
      this.searchResponse = await this.fetchFilteredMembers(this.filter, page)
    },

    async fetchMembersForExport() {
      const exportFilter = { ...this.filter }
      exportFilter.itemsPerPage = 5000
      return await this.fetchFilteredMembers(exportFilter, 1)
    },

    async fetchFilteredMembers(filters, page) {
      filters.page = page || 1
      let params = {
        firstName: filters.firstName,
        lastName: filters.lastName,
        statuses: filters.userStatuses ? filters.userStatuses.join(',') : null,
        limit: filters.itemsPerPage,
        page: filters.page,
      }
      return api.fetchUsers(params)
          .then(response => {
            return response.data
          })
          .catch(err => {
            this.handleError(err)
          })
    },

    async exportCsv() {
      this.clearMessages()
      const memberData = await this.fetchMembersForExport()
      if (memberData === undefined || memberData === null) {
        this.errorMessages.push('Error generating export data')
        return
      }

      if (memberData.page.totalElements === 0) {
        this.warningMessages.push("No members matching your filters to export.")
        return
      }
      const exportData = [
          [
              'id',
              'firstName',
              'middleName',
              'lastName',
              'email',
              'phoneNumber',
              'address',
              'addressLine2',
              'city',
              'state',
              'country',
              'postalCode',
              'isSuspended',
              'status',
              'joined',
              'created',
              'updated'
          ]
      ]

      memberData.content.forEach((member) => {
        const { names, ...row } = member
        row.status = this.userStatusMap[row.status]
        row.joined = moment(row.joined).format('MM/DD/YYYY')
        row.created = moment.utc(row.created).local().format('MM/DD/YYYY HH:mm:ss')
        if (row.updated !== null) {
          row.updated = moment.utc(row.updated).local().format('MM/DD/YYYY HH:mm:ss')
        }
        exportData.push(Object.values(row))
      })

      exportUtils.exportCsvData(exportData, 'TilyetMembersExport.csv')
    },

    handleError(err) {
      let errorMessage = api.parseResponseError(err, true)
      this.errorMessages.push(errorMessage)
    }
  },

  data() {
    return {
      search: '',
      searchResponse: {
        content: [],
        empty: true,
        page: {
          totalPages: 0,
        }
      },
      filter: {
        userStatuses: this.userStatuses || [],
        firstName: '',
        lastName: '',
        page: 1,
        itemsPerPage: 25,
      },
      filtersDialog: false,
      errorMessages: [],
      successMessages: [],
      warningMessages: [],
      userStatusMap: constants.getUserStatusMap()
    }
  }
}
</script>


<style lang="sass">
  th.member-phone-number, th.member-email, th.member-updated
    width: 12%

  th.member-status
    width: 10%

  th.member-updated, th.member-action
    width: 5%

  tr.highlight-new-member
    background-color: #F1F8E9

  span.suspended-text
    color: orangered

</style>
